import type {
  GameDefinition,
  Interaction,
  Scene,
  SceneExit,
  Dialogue,
  DialogueNode,
  DialogueChoice,
  Ending
} from '../types/game';
import {
  createEmptyState,
  addUnique,
  type GameState,
  type ConclusionRecord
} from './GameState';
import { EventBus } from './EventBus';
import { SaveManager, type StorageAdapter, type LoadResult } from './SaveManager';
import {
  evaluateCondition,
  executeActions,
  type ActionEffect,
  type ConclusionSelection
} from '../interpreter';
import { evaluateConclusion, validateSelection } from '../interpreter/conclusion';
import type {
  EngineEvent,
  EngineEventListener,
  EngineEventPayload,
  GameResult,
  MinigameResult,
  ProgressSummary
} from './events';

const MAX_EFFECT_DEPTH = 12;

/** Interacción lista para pintar, con su estado de disponibilidad. */
export interface InteractionView {
  id: string;
  label: string;
  description?: string;
  kind: Interaction['kind'];
  image?: string;
  position?: { x: number; y: number };
  area?: { x: number; y: number; w: number; h: number };
  enabled: boolean;
  disabledHint?: string;
}

export interface DialogueView {
  dialogueId: string;
  nodeId: string;
  speakerId: string;
  speakerName: string;
  portrait?: string;
  text: string;
  choices: Array<{
    id: string;
    text: string;
    enabled: boolean;
    disabledHint?: string;
  }>;
  /** true si el nodo avanza con un botón «Continuar» (sin opciones). */
  isContinue: boolean;
  isEnd: boolean;
}

export interface ObjectiveView {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  hints: string[];
  active: boolean;
}

export interface SubmitResult {
  ok: boolean;
  errors: string[];
  ending?: string;
}

export interface GameEngineOptions {
  instanceId?: string;
  storage?: StorageAdapter;
  onEvent?: EngineEventListener;
}

/**
 * Motor genérico de aventuras.
 *
 * Es headless: no conoce Phaser ni el DOM. Gestiona el estado, aplica el
 * vocabulario declarativo de condiciones/acciones y emite eventos. Las capas
 * de renderizado y un portal anfitrión lo observan y reaccionan.
 *
 * No contiene ninguna regla específica de un juego concreto: todo proviene del
 * `GameDefinition` (JSON) recibido.
 */
export class GameEngine {
  readonly game: GameDefinition;
  readonly instanceId: string;
  private state: GameState;
  private readonly bus = new EventBus();
  private readonly saves: SaveManager;
  private sequenceBlocking = false;
  private currentSequenceId: string | null = null;

  constructor(game: GameDefinition, options: GameEngineOptions = {}) {
    this.game = game;
    this.instanceId = options.instanceId ?? 'default';
    this.saves = new SaveManager(game, this.instanceId, options.storage);
    this.state = this.buildFreshState();
    if (options.onEvent) this.bus.on(options.onEvent);
  }

  /**
   * Carga una aventura desde la API por su ID.
   * Útil para aventuras creadas dinámicamente por agentes externos.
   */
  static async fromApi(adventureId: string, options: GameEngineOptions = {}): Promise<GameEngine> {
    const response = await fetch(`/api/adventures/${adventureId}`);
    if (!response.ok) {
      throw new Error(`Failed to load adventure: ${adventureId}`);
    }
    const game = await response.json();
    return new GameEngine(game, options);
  }

  /* ------------------------- ciclo de vida ------------------------- */

  private buildFreshState(): GameState {
    const state = createEmptyState({
      gameId: this.game.gameId,
      gameVersion: this.game.gameVersion,
      schemaVersion: this.game.schemaVersion,
      instanceId: this.instanceId,
      startScene: this.game.metadata.startScene
    });
    const init = this.game.initialState ?? {};
    state.variables = { ...(init.variables ?? {}) };
    state.items = [...(init.items ?? [])];
    state.evidence = [...(init.evidence ?? [])];
    return state;
  }

  on(listener: EngineEventListener): () => void {
    return this.bus.on(listener);
  }

  private emit(event: EngineEventPayload): void {
    this.bus.emit({
      ...event,
      gameId: this.game.gameId,
      gameVersion: this.game.gameVersion,
      instanceId: this.instanceId,
      at: Date.now()
    } as EngineEvent);
  }

  /** Inicia una partida nueva desde el escenario inicial. */
  newGame(): void {
    this.state = this.buildFreshState();
    this.enterScene(this.game.metadata.startScene, 0, true);
    this.emit({ type: 'gameStarted', resumed: false, scene: this.state.currentScene });
    if (this.game.metadata.introDialogue) {
      this.openDialogue(this.game.metadata.introDialogue, undefined, 0);
    }
    this.persist();
  }

  /** Intenta reanudar la partida guardada. Devuelve info sobre el resultado. */
  resumeGame(): LoadResult {
    const result = this.saves.load();
    if (result.state && !result.reason) {
      this.state = result.state;
      this.emit({ type: 'gameStarted', resumed: true, scene: this.state.currentScene });
      this.emit({ type: 'stateLoaded' });
      this.emitProgress();
      return result;
    }
    return result;
  }

  hasSave(): boolean {
    return this.saves.has();
  }

  /** Borra la partida guardada (la UI pide confirmación antes de llamar). */
  clearSave(): void {
    this.saves.clear();
  }

  saveNow(): void {
    this.persist();
  }

  private persist(): void {
    this.saves.save(this.state);
  }

  /* --------------------------- consultas --------------------------- */

  getState(): Readonly<GameState> {
    return this.state;
  }

  getMutableState(): GameState {
    return this.state;
  }

  getScene(id: string = this.state.currentScene): Scene | undefined {
    return this.game.scenes[id];
  }

  getCharacter(id: string) {
    return this.game.characters[id];
  }

  getDialogue(id: string): Dialogue | undefined {
    return this.game.dialogues[id];
  }

  isFinished(): boolean {
    return this.state.finished;
  }

  /** Si una secuencia está bloqueando la interacción. */
  isSequencePlaying(): boolean {
    return this.sequenceBlocking;
  }

  /** Notifica al motor que una secuencia ha terminado (llamado desde la UI). */
  notifySequenceEnded(): void {
    const seq = this.currentSequenceId;
    this.sequenceBlocking = false;
    this.currentSequenceId = null;
    if (seq) this.emit({ type: 'sequenceEnded', sequence: seq });
    this.saves.save(this.state);
  }

  getEnding(): Ending | undefined {
    return this.state.ending ? this.game.endings[this.state.ending] : undefined;
  }

  private isExplorationMode(): boolean {
    return this.game.mode === 'exploration';
  }

  /** En modo exploración las interacciones viven en los mapas, no en las scenes. */
  private getExplorationInteractions(): import('../types/game').ProximityInteraction[] {
    const mapId = this.state.currentScene;
    const mapDef = this.game.maps?.[mapId];
    if (!mapDef?.interactions) return [];
    return Object.values(mapDef.interactions);
  }

  /** Busca una interacción por id en el contenedor activo (mapa o escena). */
  private findInteraction(
    interactionId: string
  ): import('../types/game').ProximityInteraction | import('../types/game').Interaction | undefined {
    if (this.isExplorationMode()) {
      return this.getExplorationInteractions().find((i) => i.id === interactionId);
    }
    return this.getScene()?.interactions.find((i) => i.id === interactionId);
  }

  /**
   * Contexto de evaluación de condiciones: incluye el porcentaje del marcador
   * educativo para las condiciones `exerciseScorePct`.
   */
  private evalContext(): import('../interpreter').EvalContext {
    return { state: this.state, exerciseScorePct: this.getExerciseScore().porcentaje };
  }

  /**
   * Disponibilidad en vivo de una interacción: la misma regla que aplica
   * `getVisibleInteractions`, pero por id y sin construir la vista. La vista de
   * exploración la consulta cada pocos fotogramas porque sus marcadores se crean
   * al cargar el mapa y el estado cambia mientras se juega.
   */
  isInteractionAvailable(interactionId: string): boolean {
    const inter = this.findInteraction(interactionId);
    if (!inter) return false;
    if (inter.once && this.state.usedInteractions.includes(inter.id)) return false;
    return evaluateCondition(inter.visibleIf, this.evalContext());
  }

  /** Devuelve true si la interacción pasa su `enabledIf` (es el "siguiente paso" efectivo). */
  isInteractionEnabled(interactionId: string): boolean {
    const inter = this.findInteraction(interactionId);
    if (!inter) return false;
    return evaluateCondition(inter.enabledIf, this.evalContext());
  }

  /** Interacciones visibles (escena o mapa según el modo). */
  getVisibleInteractions(): InteractionView[] {
    const ctx = this.evalContext();
    const views: InteractionView[] = [];

    if (this.isExplorationMode()) {
      for (const inter of this.getExplorationInteractions()) {
        if (inter.once && this.state.usedInteractions.includes(inter.id)) continue;
        if (!evaluateCondition(inter.visibleIf, ctx)) continue;
        const enabled = evaluateCondition(inter.enabledIf, ctx);
        views.push({
          id: inter.id,
          label: inter.label,
          description: inter.description,
          kind: inter.kind,
          image: inter.image,
          position: inter.position,
          enabled,
          disabledHint: inter.disabledHint
        });
      }
      return views;
    }

    const scene = this.getScene();
    if (!scene) return [];
    for (const inter of scene.interactions) {
      if (inter.once && this.state.usedInteractions.includes(inter.id)) continue;
      if (!evaluateCondition(inter.visibleIf, ctx)) continue;
      const enabled = evaluateCondition(inter.enabledIf, ctx);
      views.push({
        id: inter.id,
        label: inter.label,
        description: inter.description,
        kind: inter.kind,
        image: inter.image,
        position: inter.position,
        area: inter.area,
        enabled,
        disabledHint: inter.disabledHint
      });
    }
    return views;
  }

  /** Salidas visibles desde la escena actual. */
  getVisibleExits(): SceneExit[] {
    const scene = this.getScene();
    if (!scene?.exits) return [];
    const ctx = this.evalContext();
    return scene.exits.filter((e) => evaluateCondition(e.visibleIf, ctx));
  }

  /** Vista del diálogo activo, o null si no hay conversación abierta. */
  getDialogueView(): DialogueView | null {
    const { activeDialogue, activeNode } = this.state;
    if (!activeDialogue || !activeNode) return null;
    const dlg = this.game.dialogues[activeDialogue];
    if (!dlg) return null;
    const node: DialogueNode | undefined = dlg.nodes[activeNode];
    if (!node) return null;
    const speaker = node.speaker === 'narrator' ? null : this.game.characters[node.speaker];
    const ctx = this.evalContext();
    const choices = (node.choices ?? [])
      .filter((c) => evaluateCondition(c.visibleIf, ctx))
      .map((c) => ({
        id: c.id,
        text: c.text,
        enabled: evaluateCondition(c.enabledIf, ctx),
        disabledHint: c.disabledHint
      }));
    return {
      dialogueId: activeDialogue,
      nodeId: activeNode,
      speakerId: node.speaker,
      speakerName: speaker?.name ?? 'Narrador',
      portrait: speaker?.portrait,
      text: node.text,
      choices,
      isContinue: choices.length === 0 && !node.end,
      isEnd: node.end === true
    };
  }

  getInventory() {
    return this.state.items.map((id) => this.game.items[id]).filter(Boolean);
  }

  /** Evidencias descubiertas (diario), en orden de obtención. */
  getDiary() {
    return this.state.evidence.map((id) => this.game.evidence[id]).filter(Boolean);
  }

  getObjectives(): ObjectiveView[] {
    const list = Object.values(this.game.objectives).sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    const firstPending = list.find((o) => !this.state.completedObjectives.includes(o.id));
    return list.map((o) => ({
      id: o.id,
      title: o.title,
      description: o.description,
      hints: o.hints ?? [],
      completed: this.state.completedObjectives.includes(o.id),
      active: firstPending?.id === o.id
    }));
  }

  getActiveObjective(): ObjectiveView | undefined {
    return this.getObjectives().find((o) => o.active);
  }

  /** Mapa con escenarios, estado de visita y salidas. */
  getMap() {
    return Object.values(this.game.scenes).map((s) => ({
      id: s.id,
      name: s.name ?? s.id,
      visited: this.state.visitedScenes.includes(s.id),
      current: this.state.currentScene === s.id,
      exits: (s.exits ?? []).map((e) => ({ label: e.label, to: e.to }))
    }));
  }

  getProgress(): ProgressSummary {
    const totalEvidence = Object.keys(this.game.evidence).length;
    const totalObjectives = Object.keys(this.game.objectives).length;
    const totalScenes = Object.keys(this.game.scenes).length;
    return {
      scene: this.state.currentScene,
      evidence: this.state.evidence.length,
      totalEvidence,
      objectives: this.state.completedObjectives.length,
      totalObjectives,
      visitedScenes: this.state.visitedScenes.length,
      totalScenes,
      finished: this.state.finished,
      ending: this.state.ending
    };
  }

  getResult(): GameResult | null {
    if (!this.state.ending) return null;
    const ending = this.game.endings[this.state.ending];
    if (!ending) return null;
    const score = this.getExerciseScore();
    return {
      gameId: this.game.gameId,
      gameVersion: this.game.gameVersion,
      instanceId: this.instanceId,
      ending: ending.id,
      endingTitle: ending.title,
      endingQuality: ending.quality,
      educationalSummary: ending.summary,
      evidenceCollected: [...this.state.evidence],
      objectivesCompleted: [...this.state.completedObjectives],
      hypothesis: this.state.conclusion?.hypothesis,
      measures: this.state.conclusion ? [...this.state.conclusion.measures] : undefined,
      exerciseScore: score.maximo > 0 ? score : undefined
    };
  }

  getConclusionRecord(): ConclusionRecord | null {
    return this.state.conclusion;
  }

  /* --------------------------- comandos ---------------------------- */

  /** Ejecuta una interacción de la escena/mapa actual. */
  interact(interactionId: string): void {
    if (this.state.finished) return;
    const ctx = this.evalContext();

    const inter = this.findInteraction(interactionId);
    if (!inter) return;
    if (inter.once && this.state.usedInteractions.includes(inter.id)) return;
    if (!evaluateCondition(inter.visibleIf, ctx)) return;
    if (!evaluateCondition(inter.enabledIf, ctx)) {
      if (inter.disabledHint) this.emit({ type: 'message', text: inter.disabledHint, tone: 'hint' });
      return;
    }
    if (inter.once) addUnique(this.state.usedInteractions, inter.id);
    const effects = executeActions(inter.actions, this.state);
    this.processEffects(effects, 0);
    this.emitProgress();
    this.persist();
  }

  /** Viaja a otro escenario (usado por salidas y mapa). */
  travelTo(sceneId: string): void {
    if (this.state.finished) return;
    if (!(sceneId in this.game.scenes)) return;
    this.enterScene(sceneId, 0, false);
    this.emitProgress();
    this.persist();
  }

  /** Elige una opción del diálogo activo. */
  chooseOption(choiceId: string): void {
    if (this.state.finished) return;
    const view = this.currentNode();
    if (!view) return;
    const { node } = view;
    const choice: DialogueChoice | undefined = (node.choices ?? []).find((c) => c.id === choiceId);
    if (!choice) return;
    const ctx = this.evalContext();
    if (!evaluateCondition(choice.visibleIf, ctx)) return;
    if (!evaluateCondition(choice.enabledIf, ctx)) {
      if (choice.disabledHint) this.emit({ type: 'message', text: choice.disabledHint, tone: 'hint' });
      return;
    }
    const effects = executeActions(choice.actions, this.state);
    this.processEffects(effects, 0);
    if (this.state.finished) {
      this.emitProgress();
      this.persist();
      return;
    }
    if (this.state.activeDialogue) {
      if (choice.end) {
        this.endDialogue();
      } else if (choice.next) {
        this.enterNode(choice.next, 0);
      } else {
        this.endDialogue();
      }
    }
    this.emitProgress();
    this.persist();
  }

  /** Avanza un nodo de diálogo sin opciones («Continuar»). */
  advanceDialogue(): void {
    if (this.state.finished) return;
    const view = this.currentNode();
    if (!view) return;
    const { node } = view;
    if (node.end) {
      this.endDialogue();
    } else if (node.next) {
      this.enterNode(node.next, 0);
    } else {
      this.endDialogue();
    }
    this.emitProgress();
    this.persist();
  }

  /** Cierra manualmente la conversación activa sin ejecutar consecuencias. */
  closeDialogue(): void {
    if (!this.state.activeDialogue) return;
    this.endDialogue();
    this.emitProgress();
    this.persist();
  }

  /** Ejecuta una acción individual (usada por componentes como BattleView). */
  executeAction(action: import('../types/game').Action): void {
    const effects = executeActions([action], this.state);
    this.processEffects(effects, 0);
    this.emitProgress();
    this.persist();
  }

  /** Evalúa una condición (usada por componentes como BattleView). */
  evaluateCondition(condition: import('../types/game').Condition): boolean {
    return evaluateCondition(condition, this.evalContext());
  }

  /**
   * Cierra un minijuego y aplica sus consecuencias declaradas.
   *
   * Abandonar el panel no cuenta como fracaso: no se ejecuta `onFailure`, así
   * que cerrar para revisar el escenario nunca castiga al alumno.
   */
  resolveMinigame(id: string, result: MinigameResult): void {
    const spec = this.game.minigames?.[id];
    this.emit({ type: 'minigameEnded', minigame: id, result });
    if (!spec) return;

    this.recordExerciseResult(id, spec, result);

    const actions = result.success ? spec.onSuccess : result.abandoned ? undefined : spec.onFailure;
    if (actions && actions.length > 0) {
      this.processEffects(executeActions(actions, this.state), 0);
    }
    this.emitProgress();
    this.persist();
  }

  /**
   * Registra el resultado educativo de un ejercicio evaluable.
   *
   * El puntaje se normaliza a partir del evaluador: proporción de aciertos
   * sobre respuestas emitidas, escalada a `spec.puntos`. Abandonar sin haber
   * respondido nada no registra sesión: consultar o salir nunca castiga.
   * Reintentar actualiza `last` y `best` sin duplicar recompensas.
   */
  private recordExerciseResult(id: string, spec: import('../types/game').MinigameSpec, result: MinigameResult): void {
    const max = spec.puntos;
    if (typeof max !== 'number' || max <= 0) return;
    const answered = result.aciertos + result.errores;
    if (result.abandoned && answered === 0) return;
    const score = answered > 0
      ? Math.round(max * (result.aciertos / answered))
      : 0;
    const record = this.state.exerciseResults[id];
    if (record) {
      record.last = score;
      record.best = Math.max(record.best, score);
      record.attempts += 1;
      record.max = max;
    } else {
      this.state.exerciseResults[id] = {
        first: score,
        last: score,
        best: score,
        attempts: 1,
        max
      };
    }
  }

  /**
   * Marca un contenido educativo como presentado (información vista).
   *
   * Presentar no demuestra comprensión: esto habilita ejercicios y consulta,
   * pero nunca suma puntaje académico.
   */
  resolveContent(id: string): void {
    const firstTime = addUnique(this.state.presentedContents, id);
    this.emit({ type: 'contentPresented', content: id, firstTime });
    this.emitProgress();
    this.persist();
  }

  /** Marcador educativo: mejor resultado por ejercicio sobre el total incluido. */
  getExerciseScore(): import('./events').ExerciseScoreSummary {
    const porEjercicio: import('./events').ExerciseScoreSummary['porEjercicio'] = {};
    let obtenido = 0;
    let maximo = 0;
    for (const [id, spec] of Object.entries(this.game.minigames ?? {})) {
      if (typeof spec.puntos !== 'number' || spec.puntos <= 0) continue;
      maximo += spec.puntos;
      const record = this.state.exerciseResults[id];
      porEjercicio[id] = record
        ? { ...record }
        : { first: 0, last: 0, best: 0, attempts: 0, max: spec.puntos };
      obtenido += record?.best ?? 0;
    }
    return {
      obtenido,
      maximo,
      porcentaje: maximo > 0 ? Math.round((obtenido / maximo) * 100) : null,
      porEjercicio
    };
  }

  private currentNode(): { dialogue: Dialogue; node: DialogueNode } | null {
    const { activeDialogue, activeNode } = this.state;
    if (!activeDialogue || !activeNode) return null;
    const dialogue = this.game.dialogues[activeDialogue];
    if (!dialogue) return null;
    const node = dialogue.nodes[activeNode];
    if (!node) return null;
    return { dialogue, node };
  }

  /** El motor no conoce Phaser, pero en modo exploración los mapas cambian el escenario activo. */
  enterMap(mapId: string): void {
    if (!(mapId in (this.game.maps ?? {}))) return;
    const from = this.state.currentScene;
    this.state.currentScene = mapId;
    addUnique(this.state.visitedScenes, mapId);
    this.seedObjectStates(mapId);
    if (from !== mapId) {
      this.emit({ type: 'sceneChanged', from, to: mapId });
    }
    const mapDef = this.game.maps?.[mapId];
    if (mapDef?.onEnter) {
      const effects = executeActions(mapDef.onEnter, this.state);
      this.processEffects(effects, 1);
    }
    this.emitProgress();
    this.persist();
  }

  /* --------------------- mecánica de conclusión --------------------- */

  getConclusionConfig() {
    return this.game.conclusion ?? null;
  }

  submitConclusion(selection: ConclusionSelection): SubmitResult {
    const config = this.game.conclusion;
    if (!config) return { ok: false, errors: ['Este juego no define una mecánica de conclusión.'] };
    const errors = validateSelection(config, selection, this.game);
    if (errors.length > 0) return { ok: false, errors };
    const outcome = evaluateConclusion(config, selection, this.state);
    this.state.conclusion = { ...selection, ending: outcome.ending };
    addUnique(this.state.decisions, `conclusion:${outcome.ending}`);
    this.emit({
      type: 'conclusionSubmitted',
      hypothesis: selection.hypothesis,
      evidence: [...selection.evidence],
      measures: [...selection.measures],
      ending: outcome.ending
    });
    this.doFinish(outcome.ending);
    this.emitProgress();
    this.persist();
    return { ok: true, errors: [], ending: outcome.ending };
  }

  /** Permite volver a investigar tras un desenlace con `allowContinue`. */
  continueAfterEnding(): void {
    const ending = this.getEnding();
    if (!ending || !ending.allowContinue) return;
    this.state.finished = false;
    this.state.ending = null;
    this.emitProgress();
    this.persist();
  }

  /* ------------------- flujo interno de efectos -------------------- */

  /**
   * Siembra el `initialState` declarado por los objetos del escenario.
   *
   * El estado arranca vacío, así que sin esto ninguna condición `objectState`
   * se cumple y las interacciones que dependen de un objeto en su estado
   * inicial nunca llegan a aparecer: el juego se vuelve irresoluble.
   * No pisa estados ya fijados, de modo que el progreso persistido se conserva.
   */
  private seedObjectStates(containerId: string): void {
    const objects = this.game.maps?.[containerId]?.objects ?? this.game.scenes[containerId]?.objects;
    if (!objects) return;
    for (const [id, obj] of Object.entries(objects)) {
      if (id in this.state.objectStates) continue;
      if (!(obj.initialState in obj.states)) continue;
      this.state.objectStates[id] = obj.initialState;
    }
  }

  private enterScene(sceneId: string, depth: number, isStart: boolean): void {
    const from = this.state.currentScene;
    this.state.currentScene = sceneId;
    addUnique(this.state.visitedScenes, sceneId);
    this.seedObjectStates(sceneId);
    if (!isStart || from !== sceneId) {
      this.emit({ type: 'sceneChanged', from, to: sceneId });
    }
    const scene = this.game.scenes[sceneId];
    if (scene?.onEnter) {
      const effects = executeActions(scene.onEnter, this.state);
      this.processEffects(effects, depth + 1);
    }
  }

  private openDialogue(dialogueId: string, node: string | undefined, depth: number): void {
    const dialogue = this.game.dialogues[dialogueId];
    if (!dialogue) return;
    this.state.activeDialogue = dialogueId;
    this.emit({ type: 'dialogueStarted', dialogue: dialogueId });
    this.enterNode(node ?? dialogue.startNode, depth);
  }

  private enterNode(nodeId: string, depth: number): void {
    const dialogueId = this.state.activeDialogue;
    if (!dialogueId) return;
    const dialogue = this.game.dialogues[dialogueId];
    if (!dialogue) return;
    const node = dialogue.nodes[nodeId];
    if (!node) return;
    this.state.activeNode = nodeId;
    // Las acciones del nodo se aplican una sola vez al entrar en él.
    if (node.actions) {
      const effects = executeActions(node.actions, this.state);
      this.processEffects(effects, depth + 1);
    }
  }

  private endDialogue(): void {
    const dialogue = this.state.activeDialogue;
    this.state.activeDialogue = null;
    this.state.activeNode = null;
    if (dialogue) {
      addUnique(this.state.seenDialogues, dialogue);
      this.emit({ type: 'dialogueEnded', dialogue });
    }
  }

  private doFinish(endingId: string): void {
    this.state.ending = endingId;
    this.state.finished = true;
    this.state.activeDialogue = null;
    this.state.activeNode = null;
    const result = this.getResult();
    this.emit({ type: 'gameCompleted', ending: endingId, result: result! });
  }

  private processEffects(effects: ActionEffect[], depth: number): void {
    if (depth > MAX_EFFECT_DEPTH) {
      this.emit({
        type: 'error',
        message: 'Se superó la profundidad máxima de efectos encadenados.'
      });
      return;
    }
    for (const effect of effects) {
      switch (effect.kind) {
        case 'variableSet':
          break; // el estado ya está mutado; no hay evento dedicado
        case 'itemAdded':
          this.emit({ type: 'itemAdded', item: effect.item });
          break;
        case 'itemRemoved':
          this.emit({ type: 'itemRemoved', item: effect.item });
          break;
        case 'evidenceAdded':
          this.emit({ type: 'evidenceCollected', evidence: effect.evidence });
          break;
        case 'objectiveCompleted':
          this.emit({ type: 'objectiveCompleted', objective: effect.objective });
          break;
        case 'message':
          this.emit({ type: 'message', text: effect.text, tone: effect.tone });
          break;
        case 'scene':
          this.enterScene(effect.scene, depth, false);
          break;
        case 'dialogue':
          this.openDialogue(effect.dialogue, effect.node, depth);
          break;
        case 'finish':
          this.doFinish(effect.ending);
          break;
        case 'conclusion':
          this.emit({ type: 'conclusionOpened' });
          break;
        case 'cameraZoom':
          this.emit({
            type: 'cameraRequested',
            action: 'zoom',
            target: effect.target,
            scale: effect.scale,
            duration: effect.duration,
            hold: effect.hold
          });
          break;
        case 'cameraPan':
          this.emit({
            type: 'cameraRequested',
            action: 'pan',
            to: effect.to,
            duration: effect.duration,
            hold: effect.hold
          });
          break;
        case 'objectStateChanged':
          this.emit({ type: 'objectStateChanged', object: effect.object, state: effect.state, sfx: effect.sfx });
          break;
        case 'video':
          this.emit({
            type: 'videoRequested',
            video: effect.video,
            titulo: effect.titulo,
            descripcion: effect.descripcion,
            modo: effect.modo,
            objeto: effect.objeto,
            ancho: effect.ancho,
            poster: effect.poster
          });
          break;
        case 'sequence': {
          const sceneDef = this.game.scenes[this.state.currentScene];
          const seqDef = sceneDef?.sequences?.[effect.sequence];
          if (seqDef) {
            this.sequenceBlocking = seqDef.blocking !== false;
            this.currentSequenceId = effect.sequence;
            this.emit({ type: 'sequenceStarted', sequence: effect.sequence, cinematic: seqDef.cinematic ?? false });
          }
          break;
        }
        case 'encounter': {
          this.emit({ type: 'encounterStarted', encounter: effect.encounter });
          break;
        }
        case 'minigame': {
          if (!this.game.minigames?.[effect.minigame]) break;
          this.emit({ type: 'minigameStarted', minigame: effect.minigame });
          break;
        }
        case 'content': {
          if (!this.game.contents?.[effect.content]) break;
          this.emit({ type: 'contentStarted', content: effect.content });
          break;
        }
      }
    }
  }

  private emitProgress(): void {
    this.emit({ type: 'progressChanged', summary: this.getProgress() });
  }
}
